import { useState } from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import { FaFacebookF, FaInstagram, FaSnapchatGhost } from "react-icons/fa";
import { SiTiktok } from "react-icons/si";
import emailjs from "@emailjs/browser";

emailjs.init("ARUnMMe5OdxTJurT-");

export default function ContactPage() {
  const INITIAL_FORM = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "Booking",
    message: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [status, setStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");

    try {
      await emailjs.send(
        "service_mzkljvm",
        "template_djbzv7f",
        {
          from_firstname: formData.firstName,
          from_lastname: formData.lastName,
          from_email: formData.email,
          from_phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
        },
        "ARUnMMe5OdxTJurT-"
      );

      setStatus("success");
      setFormData(INITIAL_FORM);
    } catch (err) {
      console.error("Email error:", err);
      setStatus("error");
    }
  };

  return (
    <section id="book" className="px-4 py-24 max-w-7xl mx-auto scroll-mt-24">
      <h1 className="text-4xl font-bold mb-4">
        Bestill en omvisning hos oss eller ta kontakt!
      </h1>
      <p className="text-gray-700 max-w-3xl mb-12">
        Ønsker du å lære mer om hvordan vi driver en bærekraftig ungdomsbedrift?
        Vi tar imot grupper for omvisning og deler våre erfaringer om
        entreprenørskap, gjenbruk og matsvinn. Ta kontakt for å avtale tid eller
        visst det er noe du lurer på!
      </p>

      <div className="grid md:grid-cols-2 gap-10">
        {/* Contact Info */}
        <div className="bg-black text-white rounded-xl p-6 space-y-8">
          <h2 className="text-xl font-semibold">Kontakt informasjon</h2>

          <div className="flex items-center gap-4">
            <Phone className="w-6 h-6" />
            <a href="tel:+4755108700" className="underline text-lg">
              55 10 87 00
            </a>
          </div>
          <div className="flex items-center gap-4">
            <Mail className="w-6 h-6" />
            <a
              href="mailto:visit.krokeide.ub@gmail.com"
              className="underline text-lg"
            >
              visit.krokeide.ub@gmail.com
            </a>
          </div>
          <div className="flex items-center gap-4">
            <MapPin className="w-6 h-6" />
            <a
              href="https://www.google.com/maps/place/Korsnesvegen+70,+5244+Fana"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-lg"
            >
              Korsnesvegen 70, 5244 Fana
            </a>
          </div>

          <div className="flex gap-6 pt-4 text-white text-2xl">
            <a href="https://www.facebook.com/profile.php?id=61565174009703">
              <FaFacebookF className="hover:text-pink-500 transition" />
            </a>
            <a href="https://www.snapchat.com/add/visitkrokeideub?invite_id=i5JgRuA-&locale=nb_NO&share_id=aOcYGYqhQie8z4_uIjmOUw&xp_id=1&sid=d0815fc623f04335a79e1779d2dba30d">
              <FaSnapchatGhost className="hover:text-pink-500 transition" />
            </a>
            <a href="https://www.instagram.com/visitkrokeideub/">
              <FaInstagram className="hover:text-pink-500 transition" />
            </a>
            <a href="https://www.tiktok.com/@visit.krokeide.ub1?_t=8r204lunjYO&_r=1">
              <SiTiktok className="hover:text-pink-500 transition" />
            </a>
          </div>
        </div>

        {/* Contact Form */}
        <form
          onSubmit={handleSubmit}
          className="space-y-6 bg-white shadow-lg rounded-xl p-8"
        >
          <section
            id="kontakt"
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <input
              type="text"
              name="firstName"
              placeholder="Fornavn"
              value={formData.firstName}
              onChange={handleChange}
              className="border-b-2 p-3 focus:outline-none focus:border-pink-500 transition"
              required
            />
            <input
              type="text"
              name="lastName"
              placeholder="Etternavn"
              value={formData.lastName}
              onChange={handleChange}
              className="border-b-2 p-3 focus:outline-none focus:border-pink-500 transition"
              required
            />
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              className="border-b-2 p-3 focus:outline-none focus:border-pink-500 transition"
              required
            />
            <input
              type="tel"
              name="phone"
              placeholder="Telefon"
              value={formData.phone}
              onChange={handleChange}
              className="border-b-2 p-3 focus:outline-none focus:border-pink-500 transition"
            />
          </div>

          <div>
            <p className="font-medium text-lg">Hva gjelder det?</p>
            <div className="flex gap-8 mt-1">
              {["Booking", "Spørsmål", "Annet"].map((option) => (
                <label key={option} className="flex items-center gap-2 text-lg">
                  <input
                    type="radio"
                    name="subject"
                    value={option}
                    checked={formData.subject === option}
                    onChange={handleChange}
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          <textarea
            name="message"
            placeholder="Skriv din melding her..."
            value={formData.message}
            onChange={handleChange}
            className="border-b-2 w-full p-3 h-36 focus:outline-none focus:border-pink-600 resize-none"
            required
          />

          <div className="pt-4">
            <button
              type="submit"
              className="bg-[#FD517B] text-white px-8 py-3 rounded-md hover:bg-pink-600 transition"
              disabled={status === "sending"}
            >
              {status === "sending" ? "Sender..." : "Send melding"}
            </button>
            {status === "success" && (
              <p className="text-green-600 mt-4">Melding sendt!</p>
            )}
            {status === "error" && (
              <p className="text-red-600 mt-4">Noe gikk galt. Prøv igjen.</p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
